import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: React.ReactNode
  disabled?: boolean
  desc: React.JSX.Element | string
  cancelBtnText?: string
  confirmText?: React.ReactNode
  destructive?: boolean
  handleConfirm: () => void
  isLoading?: boolean
  className?: string
  children?: React.ReactNode
  // Input field support
  showInput?: boolean
  inputValue?: string
  onInputChange?: (value: string) => void
  inputLabel?: string
  inputPlaceholder?: string
  inputRequired?: boolean
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  const {
    title,
    desc,
    children,
    className,
    confirmText,
    cancelBtnText,
    destructive,
    isLoading,
    disabled = false,
    handleConfirm,
    showInput = false,
    inputValue = '',
    onInputChange,
    inputLabel,
    inputPlaceholder = '',
    inputRequired = false,
    ...actions
  } = props

  // Determine if confirm button should be disabled
  const isConfirmDisabled = disabled || isLoading || (showInput && inputRequired && !inputValue.trim())

  return (
    <AlertDialog {...actions}>
      <AlertDialogContent className={cn(className && className)}>
        <AlertDialogHeader className='text-left'>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>{desc}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Optional input field */}
        {showInput && onInputChange && (
          <div className='space-y-2 py-2'>
            {inputLabel && (
              <Label htmlFor='confirm-dialog-input'>
                {inputLabel}
                {inputRequired && <span className='text-destructive ml-1'>*</span>}
              </Label>
            )}
            <Textarea
              id='confirm-dialog-input'
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              placeholder={inputPlaceholder}
              className='min-h-[80px] resize-none'
              disabled={isLoading}
            />
          </div>
        )}

        {children}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {cancelBtnText ?? 'Cancel'}
          </AlertDialogCancel>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {confirmText ?? 'Continue'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
